// @givmo/compliance — what the add sheet's "Next due" block says before a filing exists
// (CCM-R1-INTAKE-NEXTDUE; frames 03 and 04; the 2026-09-30 22:46 PDT ruling's option B). Pure
// functions over a decoded filing option; no React, no reads.
//
// ══ ONE DATE, CHOSEN AS THE PANEL CHOOSES ══════════════════════════════════════════════════════
//
// The picker's read serves each deadline's next date worked out for the organization's RECORDED
// facts (`next_dates`, the calendar's own date path at the server's date). A rule with two or three
// deadlines (D.C.'s licence, Maryland's exempt notice and four more) gets one block, picked in
// `libraryNextDate`'s order: the soonest date; else one waiting on the fiscal year end; else one
// waiting on a date only the organization has; else "before you first solicit" where every deadline
// is that; else no date. So the sheet and the panel that opens after adding say the same thing.
//
// ⚠ WITH NO FISCAL YEAR END RECORDED, THE REGISTER DATES NOTHING. The backend reads no profile
// without its one required key (`PROFILE_REQUIRED_PARAMETER_KEYS`), so a library filing gets no
// cycle and no occurrence until the year end is recorded, whatever its shape and whatever anchor it
// was added with. The panel then tells the filing's kind by its rule's deadline shapes alone
// (`withoutFiscalYearEnd`): no deadline, or only shapes that are never dated, is "no date"; a duty
// due before first soliciting is "Before you first solicit…"; anything else asks the year end. The
// picker's read still works some dates out from the rule alone (a fixed date, a licence period's
// shape), so the sheet gives the panel's answer instead of them (review-38's F1;
// CCM-R1-INTAKE-NODATE-FYE). Once the year end is saved, the read is asked again and the sheet takes
// the rule's own state.
//
// ══ THE WORDS ARE THE FRAMES' AND THE PANEL'S, NEVER NEW ═════════════════════════════════════════
//
//   • Frame 03's lead sentence ("Maryland’s annual report is due 6 months after your fiscal year
//     ends.") is a template, instantiated per rule for the interval shape, "and M days" included
//     (the orchestrator's Q1, 2026-10-01 19:35 PDT). Other shapes carry the panel's basis line in
//     the card instead.
//   • An anchor rule (frame 04) leads with its served `anchor.statement` and asks with its served
//     `anchor.field_label`: Jonathan's anchor words (2026-09-30 19:25 PDT).
//   • The card's lines are the panel's: the long date or "Not worked out yet", the basis, the
//     extension, the source line; "Until you add the date, …" under a date question (frame 04).
//
// ⚠ NOTHING HERE WORKS OUT A DATE. A date the organization has not recorded yet (an unsaved fiscal
// year end, a typed licence expiry) is never reflected in the card: the card reads "Not worked out
// yet" until the backend works the date out from what is recorded.

import {
  waitsOnTheFirstPeriod,
  type ComplianceProfile,
  type RuleProvenance,
} from './compliance-decode';
import { isFiscalYearAnchored } from './compliance-history-model';
import type { ComplianceFilingOption, WorkedOutDate } from './compliance-filings-decode';
import {
  anchorKindFor,
  datePassedWhileOpen,
  lacksFiscalYearEnd,
  withoutFiscalYearEnd,
  type AnchorKind,
} from './compliance-filings-model';
import {
  beforeFirstSolicitLine,
  DATE_PASSED,
  extensionLine,
  firstPeriodWaitsLine,
  isYearKeyed,
  libraryBasisLine,
  longComplianceDate,
  monthDayWords,
  NO_DATE_YET_LINE,
  NOT_WORKED_OUT_YET,
  recordedFiscalYearEnd,
  takesLicenseNote,
  UNTIL_THE_DATE_LINE,
} from './compliance-panel-model';

/** The shapes counted from a date only the organization has (the panel's `ANCHOR_BASES`). */
const ANCHOR_BASES: readonly string[] = [
  'anniversary_of_registration',
  'last_day_of_anniversary_month',
  'license_cycle_expiry',
  'registration_period_end_plus_interval',
];

/** The sheet's one date for a filing not yet added, from the option's served next dates. */
export type OptionNextDate =
  | { kind: 'dated'; entry: WorkedOutDate }
  /**
   * The date waits on the fiscal year end. `entry` is the deadline counted from it, where the rule
   * has one; with no year end recorded at all, every rule with a deadline waits on it, and a rule
   * none of whose deadlines counts from it has no `entry` (nothing to word but the question).
   */
  | { kind: 'needs-fiscal-year-end'; entry?: WorkedOutDate }
  | { kind: 'needs-date'; entry: WorkedOutDate; anchor: AnchorKind }
  | { kind: 'before-first-solicitation'; entry: WorkedOutDate }
  | { kind: 'not-supported' }
  /**
   * The first year counts from another filing's sending, which the register doesn't record yet
   * (D-CCM-110 J-F; the served `first_period`), and no last filing entered here starts it.
   */
  | { kind: 'first-period-waits'; entry: WorkedOutDate };

export function optionNextDate(
  option: ComplianceFilingOption,
  profile: ComplianceProfile | undefined,
): OptionNextDate {
  const entries = option.nextDates;
  // The register dates nothing until the fiscal year end is recorded (see the header), so neither
  // does the sheet: the panel's answer for the rule's shapes (`withoutFiscalYearEnd`), which asks
  // the year end for every rule whose date it could ever work out.
  const [first] = entries;
  if (first !== undefined && lacksFiscalYearEnd(profile)) {
    const shape = withoutFiscalYearEnd(entries.map((entry) => entry.dueDateBasis));
    if (shape === 'before-first-solicitation') {
      return { kind: 'before-first-solicitation', entry: first };
    }
    if (shape === 'not-supported') return { kind: 'not-supported' };
    const countedFromIt = entries.find((entry) => isFiscalYearAnchored([entry.dueDateBasis]));
    return countedFromIt !== undefined
      ? { kind: 'needs-fiscal-year-end', entry: countedFromIt }
      : { kind: 'needs-fiscal-year-end' };
  }
  const waits = entries.find(waitsOnTheFirstPeriod);
  if (waits !== undefined) return { kind: 'first-period-waits', entry: waits };
  const dated = entries
    .filter((entry) => entry.dueOn !== undefined)
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''))[0];
  if (dated !== undefined) return { kind: 'dated', entry: dated };
  const waitingOnTheYearEnd = entries.find(
    (entry) => entry.uncomputableReason !== undefined && isFiscalYearAnchored([entry.dueDateBasis]),
  );
  if (waitingOnTheYearEnd !== undefined) {
    return { kind: 'needs-fiscal-year-end', entry: waitingOnTheYearEnd };
  }
  const waitingOnADate = entries.find(
    (entry) => entry.uncomputableReason !== undefined && ANCHOR_BASES.includes(entry.dueDateBasis),
  );
  if (waitingOnADate !== undefined) {
    return {
      kind: 'needs-date',
      entry: waitingOnADate,
      anchor: anchorKindFor(waitingOnADate.dueDateBasis),
    };
  }
  if (
    first !== undefined &&
    entries.every((entry) => entry.dueDateBasis === 'before_first_solicitation')
  ) {
    return { kind: 'before-first-solicitation', entry: first };
  }
  return { kind: 'not-supported' };
}

/** "Annual report" → "annual report": a title read inside frame 03's sentence. */
function inSentence(title: string): string {
  return title.length > 0 ? `${title.charAt(0).toLowerCase()}${title.slice(1)}` : title;
}

/**
 * Frame 03's sentence, for the shape it was approved on (`fiscal_year_end_plus_interval`): the
 * jurisdiction's filing "is due" its interval "after your fiscal year ends", without the recorded
 * year end the card's basis would name. `undefined` for any other shape (Q1: the basis line only).
 */
export function intervalLeadSentence(
  option: ComplianceFilingOption,
  entry: WorkedOutDate,
): string | undefined {
  if (entry.dueDateBasis !== 'fiscal_year_end_plus_interval') return undefined;
  const title = option.displayTitle?.trim();
  if (title === undefined || title.length === 0) return undefined;
  const interval = libraryBasisLine({
    dueDateBasis: entry.dueDateBasis,
    sourceDeadline: entry.sourceDeadline,
    jurisdictionName: option.jurisdictionName || option.jurisdictionCode,
  });
  if (interval === undefined) return undefined;
  return `${option.jurisdictionName || option.jurisdictionCode}’s ${inSentence(title)} is due ${interval}.`;
}

/** What the add sheet's "Next due" block shows for a chosen filing (frames 03 and 04). */
export interface AddSheetNextDue {
  /** "Next renewal" for a licence that renews when it expires (frame 04); "Next due" otherwise. */
  heading: 'Next due' | 'Next renewal';
  /** The sentence above the block's question and card (frame 03's or the anchor's statement). */
  lead?: string;
  /** The fiscal year end question (frame 03): every shape counted from it. */
  asksFiscalYearEnd: boolean;
  /** The date only the organization has (frame 04), asked with the anchor's own label. */
  asksAnchor?: {
    kind: AnchorKind;
    fieldLabel: string;
    records: 'registration_date' | 'license_expires_on';
  };
  /** Frame 04's note under the field: only for D.C.'s licence-period rules (`takesLicenseNote`). */
  licenseNoteApplies: boolean;
  card: {
    lead: string;
    /** MDEXT-OPEN: N1 after the date, while the year is held open by its automatic extension. */
    leadNote?: string;
    dated: boolean;
    /**
     * CCM-R1-ADD-SHEET-NEXT-FROM-ENTRY (D-CCM-63, (A)): the read with the entered date is running,
     * so the date and its second line show as loading bars, the source kept, and the card is busy.
     */
    checking?: boolean;
    basis?: string;
    aside?: string;
    provenance: RuleProvenance;
  };
  /** Row 7's sentence under the card: wherever a worked-out date or its basis shows. */
  sentence: boolean;
}

export interface AddSheetNextDueInput {
  option: ComplianceFilingOption;
  profile: ComplianceProfile | undefined;
  /**
   * The fiscal year end the sheet's question holds, while it differs from the one recorded: the
   * card's date is not worked out for it yet, so the card says so until it is saved.
   */
  fiscalYearEndUnsaved: boolean;
  /** Today, as an ISO civil date: what N1 is read against (MDEXT-OPEN). */
  today?: string;
}

export function addSheetNextDue(input: AddSheetNextDueInput): AddSheetNextDue {
  const { option } = input;
  const next = optionNextDate(option, input.profile);
  const jurisdiction = option.jurisdictionName || option.jurisdictionCode;
  const provenance = option.provenance;
  const recordedFye = recordedFiscalYearEnd(input.profile);
  // The year end a date was worked out from (served beside it), or the one on record. Never while
  // the question holds another, unsaved one: the basis would then name a year end the person has
  // just changed.
  const yearEndOf = (entry: WorkedOutDate): string | undefined =>
    input.fiscalYearEndUnsaved
      ? undefined
      : (monthDayWords(
          entry.computedFrom.find((parameter) => parameter.parameterKey === 'fiscal_year_end')
            ?.recordedValue,
        ) ?? recordedFye);
  const basisOf = (entry: WorkedOutDate): string | undefined =>
    libraryBasisLine({
      dueDateBasis: entry.dueDateBasis,
      sourceDeadline: entry.sourceDeadline,
      fiscalYearEnd: yearEndOf(entry),
      jurisdictionName: jurisdiction,
    });
  switch (next.kind) {
    case 'dated': {
      const lead = intervalLeadSentence(option, next.entry);
      const asksFiscalYearEnd = isFiscalYearAnchored([next.entry.dueDateBasis]);
      const workedOut = !(asksFiscalYearEnd && input.fiscalYearEndUnsaved);
      return {
        heading: 'Next due',
        ...(lead !== undefined ? { lead } : {}),
        asksFiscalYearEnd,
        licenseNoteApplies: false,
        card: workedOut
          ? {
              lead: longComplianceDate(next.entry.dueOn),
              // MDEXT-OPEN: the served next date has passed while its window, counting the
              // automatic extension (served), is still open.
              ...(input.today !== undefined && datePassedWhileOpen(next.entry, input.today)
                ? { leadNote: DATE_PASSED }
                : {}),
              dated: true,
              ...(lead === undefined ? { basis: basisOf(next.entry) } : {}),
              aside: extensionLine(next.entry.extension),
              provenance,
            }
          : {
              lead: NOT_WORKED_OUT_YET,
              dated: false,
              ...(lead === undefined ? { basis: basisOf(next.entry) } : {}),
              provenance,
            },
        sentence: true,
      };
    }
    case 'needs-fiscal-year-end': {
      // The panel's view of the same state: the question, "Not worked out yet" and the source. A
      // deadline counted from the year end keeps frame 03's sentence or its basis; a rule with none
      // (a licence, a fixed date, before first soliciting, with no year end on record) shows the
      // question alone, as the panel does, until the year end is saved.
      const entry = next.entry;
      const lead = entry !== undefined ? intervalLeadSentence(option, entry) : undefined;
      return {
        heading: 'Next due',
        ...(lead !== undefined ? { lead } : {}),
        asksFiscalYearEnd: true,
        licenseNoteApplies: false,
        card: {
          lead: NOT_WORKED_OUT_YET,
          dated: false,
          ...(entry !== undefined && lead === undefined ? { basis: basisOf(entry) } : {}),
          provenance,
        },
        sentence: true,
      };
    }
    case 'needs-date': {
      const anchor = option.anchor;
      const licence = next.entry.dueDateBasis === 'license_cycle_expiry';
      return {
        heading: licence ? 'Next renewal' : 'Next due',
        ...(anchor !== undefined ? { lead: anchor.statement } : {}),
        asksFiscalYearEnd: false,
        ...(anchor !== undefined
          ? {
              asksAnchor: {
                kind: next.anchor,
                fieldLabel: anchor.fieldLabel,
                records: anchor.records,
              },
            }
          : {}),
        licenseNoteApplies: takesLicenseNote(option.jurisdictionCode, next.entry.dueDateBasis),
        card: {
          lead: NOT_WORKED_OUT_YET,
          dated: false,
          ...(anchor === undefined ? { basis: basisOf(next.entry) } : {}),
          aside: UNTIL_THE_DATE_LINE,
          provenance,
        },
        sentence: true,
      };
    }
    case 'before-first-solicitation':
      return {
        heading: 'Next due',
        asksFiscalYearEnd: false,
        licenseNoteApplies: false,
        card: { lead: beforeFirstSolicitLine(jurisdiction), dated: false, provenance },
        sentence: true,
      };
    case 'not-supported':
      return {
        heading: 'Next due',
        asksFiscalYearEnd: false,
        licenseNoteApplies: false,
        card: { lead: NO_DATE_YET_LINE, dated: false, provenance },
        sentence: false,
      };
    case 'first-period-waits':
      // C1-b (D-CCM-112) under "Not worked out yet", as the filing's panel says it.
      return {
        heading: 'Next due',
        asksFiscalYearEnd: false,
        licenseNoteApplies: false,
        card: {
          lead: NOT_WORKED_OUT_YET,
          dated: false,
          aside: firstPeriodWaitsLine(jurisdiction),
          provenance,
        },
        sentence: false,
      };
  }
}

/**
 * F4 (cut 0930-11's review; D3's map): whether a filing imported now lands on a recurring year, read
 * from the rule's served deadlines. Every deadline year-keyed means every filing of it falls on a
 * renewal year (Virginia's registration, an annual report): `renewed_filed`. A deadline that is not
 * (a licence's standing period, "before you first solicit") keeps D3's first-filing reading, since
 * the filing may be the first. With none served, the rule's class decides, as before.
 */
export function recursOnYears(option: ComplianceFilingOption): boolean {
  if (option.nextDates.length === 0) return option.ruleClass === 'annual_report';
  return option.nextDates.every((entry) => isYearKeyed(entry.cycleKey));
}

/** Where the read with the entered last filing's date stands (CCM-R1-ADD-SHEET-NEXT-FROM-ENTRY). */
export type EnteredFilingRead = 'none' | 'checking' | 'failed' | 'read';

/**
 * The card as the entered last filing leaves it (D-CCM-63): while the read with its date runs, (A),
 * the loading bars, never the old card; after a failed read, "Not worked out yet" rather than an old
 * date, with its source; otherwise the card as worked out.
 */
export function cardForTheEntry(
  card: AddSheetNextDue['card'],
  read: EnteredFilingRead,
): AddSheetNextDue['card'] {
  if (read === 'checking') return { ...card, checking: true };
  if (read === 'failed')
    return { lead: NOT_WORKED_OUT_YET, dated: false, provenance: card.provenance };
  return card;
}
